import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { Sparkline } from './Sparkline';
import { AnimatedNumber } from './AnimatedNumber';

type KpiTone =
  | 'ok' | 'info' | 'warn' | 'alert' | 'mute' | 'ink'
  | 'brand-design' | 'brand-marketing' | 'brand-product' | 'brand-talent';

interface KPIProps {
  label: string;
  /**
   * Valore della metrica. Se è un `number`, viene animato in count-up (stile
   * Stripe) tramite AnimatedNumber — coerente in tutta l'app. Se è ReactNode
   * (stringa già formattata, JSX composito) viene reso statico come prima.
   */
  value?: ReactNode | number;
  /** Formatter usato SOLO quando `value` è numerico (es. fmtEur). Default it-IT intero. */
  format?: (n: number) => string;
  /** Hero: numero-eroe a scala editoriale (ink pieno). Per la metrica dominante. */
  hero?: boolean;
  /** Attenzione: la card "respira" (pulse lento) — usare SOLO quando c'è qualcosa di urgente. */
  attn?: boolean;
  unit?: string;
  delta?: string;
  deltaDir?: 'up' | 'down';
  /**
   * Semantica colore della pill delta, disaccoppiata dalla direzione freccia.
   * - good   → verde (un miglioramento reale)
   * - bad    → rosso (richiede attenzione/azione)
   * - neutral→ grigio (info fisiologica, non un allarme)
   * Se omesso, la pill resta neutra. deltaDir controlla solo la freccia.
   */
  intent?: 'good' | 'bad' | 'neutral';
  spark?: number[];
  restricted?: boolean;
  note?: string;
  icon?: IconName;
  tone?: KpiTone;
  /** Pattern P4 — KPI cliccabile = filtro: quando presente la card diventa interattiva */
  onClick?: () => void;
  /** Stato attivo del filtro associato (aria-pressed) */
  active?: boolean;
}

export function KPI({ label, value, format, hero, attn, unit, delta, deltaDir, intent, spark, restricted, note, icon, tone, onClick, active }: KPIProps) {
  if (restricted) {
    return (
      <div className="kpi restricted" data-tone="mute">
        <div className="kpi-icon"><Icon name="lock" size={18} /></div>
        <div className="kpi-label"><span>{label}</span></div>
        <div className="kpi-value mute-2">•••••</div>
        <div className="kpi-delta mute">Super Admin</div>
      </div>
    );
  }
  const isNum = typeof value === 'number';
  const interactive = !!onClick;
  return (
    <div
      className={`kpi${interactive ? ' cursor-pointer' : ''}`}
      data-tone={tone ?? 'mute'}
      data-hero={hero ? '' : undefined}
      data-attn={attn ? '' : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? !!active : undefined}
      onClick={onClick}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick!(); } } : undefined}
    >
      {icon && (
        <div className="kpi-icon"><Icon name={icon} size={18} /></div>
      )}
      <div className="kpi-label">
        <span>{label}</span>
        {note && <span className="mute font-normal normal-case text-[12px] tracking-normal">{note}</span>}
      </div>
      <div className="kpi-value">
        {unit && <span className="unit">{unit}</span>}
        {isNum ? <AnimatedNumber value={value as number} format={format} duration={420} /> : value}
      </div>
      {delta && (
        <div className={`kpi-delta ${intent ?? deltaDir ?? ''}`}>
          {intent !== 'neutral' && deltaDir === 'up' && <Icon name="arrowUp" size={12} />}
          {intent !== 'neutral' && deltaDir === 'down' && <Icon name="arrowDown" size={12} />}
          <span>{delta}</span>
        </div>
      )}
      {spark && (
        <div className="kpi-spark">
          <Sparkline data={spark} />
        </div>
      )}
    </div>
  );
}
